import React, { useEffect, useState } from "react";
import { Box, IconButton, Tooltip, Theme, useTheme } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";

export const getShippingLabelUrl = (labelS3Key?: string | null) => {
  const key = labelS3Key?.trim();
  if (!key) return "";
  return `${process.env.REACT_APP_CONTENT_CLOUDFRONT_URL}/${key}?response-content-disposition=inline%3B%20filename%3D%22${key}`;
};

export interface LabelSecondaryProps {
  labelS3Key?: string | null;
  prefix?: React.ReactNode;
  emptyFallback?: string;
  fontSize?: string | number;
}

const LabelSecondary: React.FC<LabelSecondaryProps> = ({
  labelS3Key,
  prefix,
  emptyFallback = "-",
  fontSize = "0.75rem",
}) => {
  const theme = useTheme<Theme>();
  const colors = theme.palette.customColors;
  const [copied, setCopied] = useState(false);
  const labelUrl = getShippingLabelUrl(labelS3Key);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (!labelUrl) return;
    try {
      await navigator.clipboard.writeText(labelUrl);
      setCopied(true);
    } catch {
      // Clipboard may be blocked; the label link remains available.
    }
  };

  const labelValue = labelUrl ? (
    <Box
      component="a"
      href={labelUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
      sx={{
        color: colors.darkBlue,
        textDecoration: "underline",
        fontWeight: 500,
      }}
    >
      View
    </Box>
  ) : (
    <Box component="span">{emptyFallback}</Box>
  );

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        minWidth: 0,
        maxWidth: "100%",
        fontSize,
      }}
    >
      {prefix}
      {labelValue}
      {labelUrl ? (
        <Tooltip title={copied ? "Copied" : "Copy label URL"}>
          <IconButton
            size="small"
            onClick={(event) => {
              void handleCopy(event);
            }}
            aria-label="Copy shipping label URL"
            sx={{
              p: 0.3,
              flexShrink: 0,
              color: copied ? colors.purpleMain : colors.greyTextColor,
            }}
          >
            {copied ? (
              <CheckIcon sx={{ fontSize: 12 }} />
            ) : (
              <ContentCopyOutlinedIcon sx={{ fontSize: 12 }} />
            )}
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
};

export default LabelSecondary;
